import React, { useRef } from 'react';
import { X, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface InputImageProps {
  id: string;
  name: string;
  label: string;
  imagePreview?: string | null;
  onImageChange: (file: File) => void;
  onRemoveImage: () => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
}

/**
 * InputImage - Composant pour l'upload et la prévisualisation d'images
 */
const InputImage: React.FC<InputImageProps> = ({
  id,
  name,
  label,
  imagePreview,
  onImageChange,
  onRemoveImage,
  required = false,
  disabled = false,
  className,
  labelClassName
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImageChange(file);
    }
  };

  const handleRemoveClick = () => {
    onRemoveImage();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`mb-4 ${className || ''}`}>
      <label htmlFor={id} className={`block text-sm font-medium mb-1 ${labelClassName || ''}`}>
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-md p-4 text-center">
        {imagePreview ? (
          <div className="relative">
            <img 
              src={imagePreview} 
              alt="Aperçu" 
              className="max-h-40 mx-auto rounded-md"
            />
            <button 
              type="button"
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
              onClick={handleRemoveClick}
              disabled={disabled}
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div 
            className="py-4 cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <ImageIcon size={32} className="mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('common.imageUploadPlaceholder', 'Glissez une image ou cliquez pour parcourir')}
            </p>
            <input 
              type="file" 
              id={id}
              name={name}
              ref={fileInputRef}
              className="hidden" 
              accept="image/*"
              onChange={handleFileChange}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InputImage;
